import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Input, 
  Select, 
  Card, 
  Tag, 
  Typography,
  message,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Modal
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { lawService } from '../../services/lawService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Law, LawCategory, LawStatus, SearchType, SearchField, Attachment } from '../../types';
import { formatLocalizedDate } from '../../utils/dateUtils';
import { showLoadError } from '../common/ErrorMessage';
import { LAWS_KEYS, COMMON_KEYS } from '../../i18n/translationKeys';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const LawList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [laws, setLaws] = useState<Law[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<SearchType>(SearchType.SIMPLE);
  const [searchField, setSearchField] = useState<SearchField>(SearchField.BOTH);
  const [approvedOnly, setApprovedOnly] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // 화면 크기 감지
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const fetchLaws = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        // 관리자는 모든 규범류를 볼 수 있음
        const data = await lawService.searchLaws({
          limit: 50,
          sortBy: 'category_priority',
          sortOrder: 'ASC',
        });
        // 교헌 먼저, 나머지는 제목 오름차순
        const kyohun = data.laws.filter(law => law.category === '교헌');
        const others = data.laws.filter(law => law.category !== '교헌').sort((a, b) => a.title.localeCompare(b.title, 'ko'));
        setLaws([...kyohun, ...others]);
      } else {
        // 일반 사용자는 승인된 규범류와 미승인 규범류의 마지막 승인된 버전을 볼 수 있음
        const data = await lawService.getLawsForGeneralUser();
        setLaws(data);
      }
    } catch (error) {
      showLoadError('규범류 목록');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchLaws();
  }, [fetchLaws]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      fetchLaws();
      return;
    }

    setLoading(true);
    try {
      const data = await lawService.searchLaws({
        query: searchQuery,
        searchType,
        searchField,
        approvedOnly: isAdmin ? approvedOnly : true,
        limit: 50,
      });
      setLaws(data.laws);
    } catch (error) {
      message.error(t(LAWS_KEYS.SEARCH_FAILED));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchType, searchField, approvedOnly, t, isAdmin, fetchLaws]);

  const handleApprove = async (lawId: string, currentApproved: boolean) => {
    try {
      await lawService.approveLaw(lawId, { keepHistory: true });
      message.success(currentApproved ? '승인이 취소되었습니다.' : '승인되었습니다.');
      fetchLaws();
    } catch (error: any) {
      message.error(error.response?.data?.message || '승인 처리에 실패했습니다.');
    }
  };

  const handleDelete = async (lawId: string) => {
    Modal.confirm({
      title: t(LAWS_KEYS.DELETE_TITLE),
      content: t(LAWS_KEYS.CONFIRM_DELETE),
      okText: t(COMMON_KEYS.DELETE),
      cancelText: t(COMMON_KEYS.CANCEL),
      onOk: async () => {
        try {
          await lawService.deleteLaw(lawId);
          message.success(t(LAWS_KEYS.DELETE_SUCCESS));
          fetchLaws();
        } catch (error: any) {
          message.error(error.response?.data?.message || t(LAWS_KEYS.DELETE_FAILED));
        }
      },
    });
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      message.error(t(LAWS_KEYS.DOWNLOAD_FAILED));
    }
  };

  const getCategoryColor = (category: LawCategory) => {
    switch (category) {
      case LawCategory.KYOHUN:
        return 'blue';
      case LawCategory.KYOGYU:
        return 'green';
      case LawCategory.KYORYEONG:
        return 'orange';
      case LawCategory.YEGYU:
        return 'purple';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return 'green';
      case LawStatus.AMENDED:
        return 'blue';
      case LawStatus.FULLY_AMENDED:
        return 'purple';
      case LawStatus.ABOLISHED:
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return t(LAWS_KEYS.ENACTED);
      case LawStatus.AMENDED:
        return t(LAWS_KEYS.AMENDED);
      case LawStatus.FULLY_AMENDED:
        return t(LAWS_KEYS.FULLY_AMENDED);
      case LawStatus.ABOLISHED:
        return t(LAWS_KEYS.ABOLISHED);
      default:
        return status;
    }
  };

  const getCategoryText = (category: LawCategory) => {
    switch (category) {
      case LawCategory.KYOHUN: return t(LAWS_KEYS.KYOHUN);
      case LawCategory.KYOGYU: return t(LAWS_KEYS.KYOGYU);
      case LawCategory.KYORYEONG: return t(LAWS_KEYS.KYORYEONG);
      case LawCategory.YEGYU: return t(LAWS_KEYS.YEGYU);
      default: return category;
    }
  };

  const columns = [
    {
      title: t(LAWS_KEYS.CATEGORY),
      dataIndex: 'category',
      key: 'category',
      width: isMobile ? 60 : 70,
      align: 'center' as const,
      filters: isAdmin ? [
        { text: t(LAWS_KEYS.KYOHUN), value: LawCategory.KYOHUN },
        { text: t(LAWS_KEYS.KYOGYU), value: LawCategory.KYOGYU },
        { text: t(LAWS_KEYS.KYORYEONG), value: LawCategory.KYORYEONG },
        { text: t(LAWS_KEYS.YEGYU), value: LawCategory.YEGYU },
      ] : undefined,
      onFilter: isAdmin ? (value: any, record: Law) => record.category === String(value) : undefined,
      render: (category: LawCategory) => <Tag color={getCategoryColor(category)}>{getCategoryText(category)}</Tag>,
    },
    {
      title: t(LAWS_KEYS.SERIAL_NUMBER),
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: isMobile ? 70 : 80,
      align: 'center' as const,
      render: (number: number, record: Law) => {
        // 일련번호가 없거나 0인 경우 공백으로 표시
        if (!number || number === 0) {
          return '';
        }
        // 관리처가 있는 경우 2줄로 표시
        if (record.owner) {
          return (
            <span style={{ display: 'inline-block', lineHeight: 1.2 }}>
              <div>{record.owner}</div>
              <div>{`제${number}호`}</div>
            </span>
          );
        }
        // 일련번호만 있는 경우 기존대로 표시
        const format = t(LAWS_KEYS.SERIAL_NUMBER_FORMAT);
        return format.replace('{number}', number.toString());
      },
    },
    {
      title: t(LAWS_KEYS.STATUS),
      dataIndex: 'status',
      key: 'status',
      width: isMobile ? 100 : 110,
      align: 'center' as const,
      render: (status: LawStatus) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: t(LAWS_KEYS.TABLE_TITLE),
      dataIndex: 'title',
      key: 'title',
      width: isMobile ? 200 : 250,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string, record: Law) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/laws/${record.law_id}`)}
          style={{ 
            padding: 0, 
            height: 'auto', 
            textAlign: 'left',
            maxWidth: '100%',
            overflow: 'hidden',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: '1.4'
          }}
          title={text}
        >
          {text}
        </Button>
      ),
    },
    {
      title: t(LAWS_KEYS.ATTACHMENTS),
      dataIndex: 'attachments',
      key: 'attachments',
      width: isMobile ? 60 : 70,
      align: 'center' as const,
      render: (attachments: Attachment[], record: Law) => {
        if (!attachments || attachments.length === 0) {
          return null;
        }
        
        return (
          <Space size="small">
            {attachments.slice(0, 3).map((attachment) => (
              <Button
                key={attachment.attachment_id}
                type="text"
                size="small"
                icon={<span style={{ fontSize: '16px' }}>📎</span>}
                onClick={() => handleDownloadAttachment(attachment)}
                style={{ 
                  padding: '2px 4px', 
                  minWidth: 'auto',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={decodeURIComponent(attachment.file_name)}
              />
            ))}
            {attachments.length > 3 && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                +{attachments.length - 3}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: t(LAWS_KEYS.RESOLUTION_DATE),
      dataIndex: 'resolution_date',
      key: 'resolution_date',
      width: isMobile ? 100 : 110,
      align: 'center' as const,
      render: (date: string) => date ? formatLocalizedDate(date) : '',
    },
    {
      title: t(LAWS_KEYS.VIEW_COUNT),
      dataIndex: 'view_count',
      key: 'view_count',
      width: isMobile ? 60 : 70,
      align: 'center' as const,
      render: (count: number) => count.toLocaleString(),
    },
    // 관리자만 볼 수 있는 컬럼들
    ...(isAdmin ? [
      {
        title: t(LAWS_KEYS.IS_APPROVED),
        dataIndex: 'is_approved',
        key: 'is_approved',
        width: isMobile ? 60 : 70,
        align: 'center' as const,
        render: (isApproved: boolean) => (
          <Tag color={isApproved ? 'green' : 'orange'}>
            {isApproved ? t(LAWS_KEYS.APPROVED) : t(LAWS_KEYS.UNAPPROVED)}
          </Tag>
        ),
      },
      {
        title: t('common.actions'),
        key: 'actions',
        width: isMobile ? 80 : 90,
        align: 'center' as const,
        render: (_: any, record: Law) => (
          <Space size="small">
            <Tooltip title={t(COMMON_KEYS.VIEW)}>
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/laws/${record.law_id}`)}
                style={{ padding: '2px 4px', minWidth: 'auto' }}
              />
            </Tooltip>
            <Tooltip title={t(COMMON_KEYS.EDIT)}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/laws/${record.law_id}/edit`)}
                style={{ padding: '2px 4px', minWidth: 'auto' }}
              />
            </Tooltip>
                         <Tooltip title={record.is_approved ? t(LAWS_KEYS.AMENDED) : t(COMMON_KEYS.EDIT)}>
              <Button
                type="text"
                size="small"
                icon={record.is_approved ? <StopOutlined /> : <CheckCircleOutlined />}
                onClick={() => handleApprove(record.law_id, record.is_approved)}
                style={{ padding: '2px 4px', minWidth: 'auto' }}
              />
            </Tooltip>
            <Popconfirm
                             title={t(LAWS_KEYS.CONFIRM_DELETE)}
              onConfirm={() => handleDelete(record.law_id)}
              okText={t(COMMON_KEYS.CONFIRM)}
              cancelText={t(COMMON_KEYS.CANCEL)}
            >
              <Tooltip title={t(COMMON_KEYS.DELETE)}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ] : []),
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card style={{ padding: '16px' }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={2}>
            {t(LAWS_KEYS.TITLE)}
          </Title>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          {isAdmin && (
            <Title level={3} style={{ margin: 0 }}>
              {t(LAWS_KEYS.LIST)}
            </Title>
          )}
          {isAdmin && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/laws/create')}
            >
              {t(LAWS_KEYS.CREATE)}
            </Button>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={8} lg={6}>
              <Search
                placeholder={i18n.language === 'ko' ? '검색' : 'Search'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={handleSearch}
                style={{ width: '100%' }}
                enterButton={<SearchOutlined />}
              />
            </Col>
            {isAdmin && (
              <>

                <Col xs={12} sm={8} md={4} lg={3}>
                  <Select
                    value={searchType}
                    onChange={setSearchType}
                    style={{ width: '100%' }}
                  >
                    <Option value={SearchType.SIMPLE}>{t(LAWS_KEYS.SIMPLE_SEARCH)}</Option>
                    <Option value={SearchType.FULL_TEXT}>{t(LAWS_KEYS.FULL_TEXT_SEARCH)}</Option>
                    <Option value={SearchType.ADVANCED}>{t(LAWS_KEYS.ADVANCED_SEARCH)}</Option>
                  </Select>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                  <Select
                    value={searchField}
                    onChange={setSearchField}
                    style={{ width: '100%' }}
                  >
                    <Option value={SearchField.BOTH}>{t(LAWS_KEYS.ALL)}</Option>
                    <Option value={SearchField.TITLE}>{t(LAWS_KEYS.TABLE_TITLE)}</Option>
                    <Option value={SearchField.CONTENT}>{t(LAWS_KEYS.CONTENT)}</Option>
                  </Select>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                  <Button
                    type={approvedOnly ? 'primary' : 'default'}
                    onClick={() => setApprovedOnly(!approvedOnly)}
                    style={{ width: '100%' }}
                  >
                    {t(LAWS_KEYS.APPROVED_ONLY)}
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={laws}
          rowKey="law_id"
          loading={loading}
          scroll={{ 
            x: isMobile ? 500 : 600,
            scrollToFirstRowOnChange: false
          }}
          pagination={{
            pageSize: 50,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${t(COMMON_KEYS.TOTAL)} ${total}`,
            size: isMobile ? 'small' : 'default',
          }}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>
    </div>
  );
};

export default LawList; 